"use client"

import React, { useActionState, useState } from "react"
import Form from "next/form"
import Link from "next/link"
import { addOrderItemsToWishlistAction, createWishlistAction } from "@/actions/wishlistActions"
import { ClipboardList, LoaderCircle } from "lucide-react"
import { useForm } from "react-hook-form"

import { hasEntries } from "@/lib/helper"
import { getLinkWithPrefixFromURLAndLocale } from "@/lib/link-utils/link-utils"
import { zodResolver } from "@/lib/polyfill/zodResolver"
import { OrderLineItemData } from "@/lib/storefront/order/order"
import {
  Button,
  buttonVariants,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Input,
} from "@/components/ui/components"
import { cn } from "@/components/ui/helpers/utils"
import type { OrderDetailBlok } from "./types"
import { CreateWishlistSchema, safeParse } from "@/components/wishlist/CreateWishlist"

interface AddToWishlistDialogProps {
  blok: OrderDetailBlok
  wishlistViewUrl: string
  sbLocale: string
  orderLineItems: OrderLineItemData[]
}

export default function AddToWishlistDialog(props: AddToWishlistDialogProps) {
  const [createDialogIsOpen, setCreateDialogIsOpen] = useState(false)
  const [confirmDialogIsOpen, setConfirmDialogIsOpen] = useState(false)
  const [confirmDialogMessage, setConfirmDialogMessage] = useState<string>(
    props.blok?.label_wishlist_confirm_dialog_message ?? ""
  )

  const [wishlistName, setWishlistName] = useState("")
  const [wishlistId, setWishlistId] = useState("")

  const resolver = zodResolver(CreateWishlistSchema)

  async function formAction(_: any, formData: FormData) {
    const values = {
      name: formData.get("name") as string,
    }
    const errors = safeParse(values)
    if (!hasEntries(errors)) {
      const wishlistName = values.name
      createWishlistAction(wishlistName)
        .then((wishlistId) => {
          addOrderItemsToWishlistAction(wishlistId, props.orderLineItems)
          return wishlistId
        })
        .then((wishlistId) => {
          setConfirmDialogMessage(
            confirmDialogMessage
              ? JSON.parse(JSON.stringify(confirmDialogMessage).replace("%1", wishlistName))
              : confirmDialogMessage
          )
          setWishlistName(wishlistName)
          setWishlistId(wishlistId)
          setCreateDialogIsOpen(false)
          setConfirmDialogIsOpen(true)
        })
        .catch((err) => {
          console.error(err, wishlistName)
          errors["name"] = {}
        })
    }
    return {
      values,
      errors,
    }
  }

  const [state, action, isPending] = useActionState(formAction, {})

  const { formState, register } = useForm({
    errors: state.errors,
    mode: "onChange",
    resolver,
  })

  function onCreateDialogOpenChange(open: boolean) {
    setCreateDialogIsOpen(open)
  }

  return (
    <>
      <Dialog open={createDialogIsOpen} onOpenChange={onCreateDialogOpenChange}>
        <DialogTrigger asChild={true}>
          <Button size={"smNoGrow"} variant={"outline"} className="bg-primary text-white">
            <ClipboardList className={"size-5"} />
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{props.blok.label_wishlist_dialog_title ?? "Als neue Merkliste speichern"}</DialogTitle>
          </DialogHeader>
          <Form className={"flex flex-col gap-4"} action={action}>
            <Input
              type="text"
              placeholder={props.blok.label_wishlist_dialog_name_placeholder ?? "Name der Merkliste*"}
              {...register("name")}
            />
            {formState.errors.name ? (
              <p className={"text-sm text-primary"} role="alert">
                {formState.errors.name.type === "too_big"
                  ? (props.blok.label_wishlist_dialog_name_too_big ?? "Name zu lang")
                  : (props.blok.label_wishlist_dialog_name_too_small ?? "Name zu kurz")}
              </p>
            ) : null}
            <Button className={cn(buttonVariants({ size: "default" }))} disabled={isPending}>
              {!isPending ? (
                <span>{props.blok?.label_wishlist_dialog_submit ?? "Anlegen"}</span>
              ) : (
                <LoaderCircle className={"animate-spin"} />
              )}
            </Button>
          </Form>
        </DialogContent>
      </Dialog>
      <Dialog open={confirmDialogIsOpen} onOpenChange={setConfirmDialogIsOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{props.blok.wishlist_confirm_dialog_title}</DialogTitle>
            <DialogDescription>
              {confirmDialogMessage}
              <br />
              {(props.blok?.label_wishlist_confirm_dialog_link_label ?? "Merkliste") && wishlistName && wishlistId && (
                <>
                  {props.blok.label_wishlist_confirm_dialog_link_label}{" "}
                  <Link
                    href={getLinkWithPrefixFromURLAndLocale(`/account/wishlist/${wishlistId}`, props.sbLocale)}
                    prefetch={false}
                    className={"underline"}
                  >
                    {wishlistName}
                  </Link>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogClose className={cn(buttonVariants({ size: "default" }))}>
            {props.blok?.label_wishlist_confirm_dialog_submit ?? "Bestätigen"}
          </DialogClose>
        </DialogContent>
      </Dialog>
    </>
  )
}
